<template>
  <section>
    <OrderStatusDashboard
        v-if="showOrderStatusDashboard"
        :orders="orderStatusRows"
        :kpi-data="orderStatusKpiData"
        :kpi-config="orderStatusKpiConfig"
        :report-header-map="orderStatusHeaderMap"
        :use-ordered-header-suffix="isMobileDevice"
        :order-dashboard-title="orderDashboardTitle"
    />
    <div class="sales-title-row">
      <div class="sales-left-title">{{ salesDashboardTitle }}</div>
      <div class="key-row">
        <div
            v-for="(item, key) in viz1.viz_specific7.sub_data_sql"
            :key="key"
            class="key-item"
            @click="handleKeyClick(key)"
            :style="{ color: activeKey === key ? 'rgb(0, 105, 217)' : '' }"
        >
          {{ item.display_name }}
        </div>
      </div>
      <div></div>
    </div>
    <div class="sales-dashboard-container">
      <!-- Sales Dashboard Header -->
      <div class="sales-dashboard-header">
        <div class="time-period-selector">
          <DatePicker
              v-model="dateRange"
              :class="{ 'selected': selectedVal === 'Date Range' }"
              :multiple="true"
              :confirm="true"
              placeholder="Date Range"
              :disabled-date="disableFutureDates"
              :enable-time-picker="false"
              :clearable="true"
              :style="datepickerStyle"
              class="responsive-datepicker"
          />
        </div>
        <div class="comment-button" v-if="hasValidActions">
          <i class="fas fa-comment" @click="commentBox"></i>
        </div>
      </div>
      <b-modal
          id="comment-modal"
          title="Add Note"
          centered
          hide-footer
          @shown="initForm"
          @hidden="resetForm"
      >
        <div class="popup-content">

          <div
              v-for="action in viz1.viz_specific7.actions"
              :key="action.input_key"
              class="mb-3"
          >
            <b-form-group :label="action.name + ' :'">

              <!-- TEXTAREA -->
              <b-form-textarea
                  v-if="action.action_type === 'textarea'"
                  v-model="formData[action.input_key]"
                  rows="4"
                  placeholder="Enter here..."
              />

              <!-- DROPDOWN (v-select) -->
              <v-select
                  v-else-if="action.action_type === 'dropdown'"
                  v-model="formData[action.input_key]"
                  :options="action.input_values"
                  placeholder="Select"
                  :clearable="true"
              />

              <!-- RADIO -->
              <b-form-radio-group
                  v-else-if="action.action_type === 'radio'"
                  v-model="formData[action.input_key]"
                  :options="action.input_values"
                  :name="action.input_key"
              />

              <!-- DATE PICKER -->
              <date-picker
                  v-else-if="action.action_type === 'datepicker'"
                  v-model="formData[action.input_key]"
                  format="YYYY-MM-DD"
                  value-type="format"
                  placeholder="Select date"
                  style="width: 100%"
              />

              <!-- STAR RATING -->
              <div v-else-if="action.action_type === 'star'" class="star-rating">
          <span
              v-for="star in 5"
              :key="star"
              class="star"
              :class="{ selected: star <= (formData[action.input_key] || 0) }"
              @click="formData[action.input_key] = star"
          >
            ★
          </span>
              </div>

              <!-- FALLBACK INPUT -->
              <b-form-input
                  v-else
                  v-model="formData[action.input_key]"
                  placeholder="Enter value"
              />

            </b-form-group>
          </div>

          <div class="d-flex justify-content-end mt-4 border-top pt-3">
            <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-2"
                @click="resetForm"
            >
              Reset
            </b-button>

            <b-button
                size="sm"
                variant="primary"
                @click="onSubmit"
            >
              Submit
            </b-button>
          </div>

        </div>
      </b-modal>


      <!-- Main Content Area -->
      <div class="dashboard-content">
        <!-- Schema Display -->
        <div class="schema-display" v-if="selectedVal==='Date Range'">
          <p class="date-range-label">
            {{ new Date(dateRange[0]).toLocaleDateString('en-GB') }} to
            {{ new Date(dateRange[dateRange.length - 1]).toLocaleDateString('en-GB') }}
          </p>
        </div>

        <!-- Sales Data Display -->
        <div class="sales-data-container">
          <!-- Aggregated totals for parent_headers with aggregation: true -->
          <div v-if="hasAggregationTotals" class="aggregation-summary">
            <div class="product-title-container aggregation-spacer"></div>
            <div class="product-metrics">
              <!-- Date Range: only metric1 total -->
              <div v-if="isDateRange" class="metric aggregation-item">
                <span class="metric-value">{{ formatMetric(dateRangeMetric1Total) }}</span>
              </div>
              <template v-else>
                <div
                    v-for="(agg, i) in aggregationTotals"
                    :key="i"
                    class="metric aggregation-item"
                    :class="{
                      'aggregation-empty': agg.empty,
                      'green-text': agg.isPercent && agg.diff > 0,
                      'red-text': agg.isPercent && agg.diff < 0,
                      'grey-text': agg.isPercent && !agg.diff
                    }"
                >
                  <template v-if="agg.isPercent">{{ agg.display }}</template>
                  <template v-else-if="!agg.empty">
                    <span v-if="agg.title" class="metric-label">{{ agg.title }}</span>
                    <span class="metric-value">{{ formatMetric(agg.total) }}</span>
                  </template>
                </div>
              </template>
            </div>
          </div>
          <!-- Non-Date Range View -->
          <div class="product-list">
            <div class="product-item" v-for="(item, index) in segregatedData" :key="index">
              <div class="product-header" @click="toggleCollapse(index,item)">
                <div class="product-title-container">
                  <img class="toggle-icon" src="@/assets/menu-bar.png" alt="toggle"/>
                  <span class="product-name">{{ item.dim2_name }}</span>
                </div>
                <!-- Date Range: only metric1, no curr/prev/growth -->
                <div v-if="isDateRange" class="product-metrics">
                  <div class="metric">
                    <span class="metric-value" :class="{ 'grey-text': !item.metric1 }">
                      {{ formatMetric(item.metric1) }}
                    </span>
                  </div>
                </div>
                <div
                    v-else-if="viz1.viz_specific7.parent_headers && viz1.viz_specific7.parent_headers.length"
                    class="product-metrics"
                >
                  <div
                      class="metric"
                      v-for="(child, index) in viz1.viz_specific7.parent_headers"
                      :key="index"
                  >
                    <!-- Normal metrics -->
                    <template v-if="child.title !== 'percent'">
                      <span v-if="child.title ==='time_period'"
                            class="metric-label">{{ getPeriodLabel(child.period)[child.value] }}</span>
                      <span v-else class="metric-label">{{ child.title }}:</span>
                      <span
                          v-if="child.title==='time_period'"
                          class="metric-value"
                          :class="{ 'grey-text': !item[child.metric] }"
                      >
                        {{ formatMetric(getMetricValue(item, child.period, child.value)) }}
                      </span>
                      <span
                          v-else
                          class="metric-value"
                          :class="{ 'grey-text': !item[child.metric] }"
                      >
                        {{ formatMetric(item[child.metric]) }}
                      </span>
                    </template>

                    <!-- Percent metric special case -->
                    <template v-else>
                      <span class="metric-label"></span>
                      <span
                          class="metric-value"
                          :class="{
                            'green-text': (item[viz1.viz_specific7.parent_headers[0].metric] - item[viz1.viz_specific7.parent_headers[1].metric]) > 0,
                            'red-text': (item[viz1.viz_specific7.parent_headers[0].metric] - item[viz1.viz_specific7.parent_headers[1].metric]) < 0,
                            'grey-text': !(item[viz1.viz_specific7.parent_headers[0].metric] - item[viz1.viz_specific7.parent_headers[1].metric])
                          }"
                      >
                        {{
                          calculateGrowth(
                              item[viz1.viz_specific7.parent_headers[0].metric],
                              item[viz1.viz_specific7.parent_headers[1].metric]
                          )
                        }}%
                      </span>
                    </template>
                  </div>
                </div>

                <div v-else class="product-metrics">
                  <div class="metric">
                    <span class="metric-label">Target:</span>
                    <span class="metric-value" :class="{'grey-text': !item.metric15}">
                      {{ formatMetric(item.metric15) }}
                    </span>
                  </div>
                  <div class="metric">
                    <span class="metric-label">Achieved:</span>
                    <span class="metric-value" :class="{'grey-text': !item.metric1}">
                      {{ formatMetric(item.metric1) }}
                    </span>
                  </div>
                  <div class="metric">
                    <span class="metric-label"></span>
                    <span class="metric-value" :class="{
                      'green-text': (item.metric1-item.metric15) > 0,
                      'red-text': (item.metric1-item.metric15) < 0,
                      'grey-text': (item.metric1-item.metric15)
                    }">
                      {{ calAchievedPerc(item.metric15, item.metric1) }}%
                    </span>
                  </div>
                </div>
              </div>

              <b-collapse :visible="isOpen[index]" class="product-details">
                <div v-if="selectedVal!=='Date Range'" class="metrics-grid-container">
                  <!-- Header Row: desktop aligns with level2 sub_parent_headers -->
                  <div class="grid-row grid-header">
                    <div class="grid-row-spacer"></div>
                    <div class="grid-row-metrics">
                      <div class="grid-cell">Current</div>
                      <div class="grid-cell">Previous</div>
                      <div class="grid-cell">Growth</div>
                    </div>
                  </div>

                  <!-- Data Rows -->
                  <div
                      class="grid-row"
                      v-for="period in viz1.viz_specific7.time_periods"
                      :key="period"
                  >
                    <div class="grid-row-spacer"></div>
                    <div class="grid-row-metrics">
                      <div
                          class="grid-cell"
                          :class="{ 'na-text': !getMetricValue(item, period, 'curr') }"
                      >
                        <div class="grid-cell-inner">
                          <span>{{ getPeriodLabel(period).curr }}</span>
                          <span class="numcompare">{{ formatMetric(getMetricValue(item, period, 'curr')) }}</span>
                        </div>
                      </div>

                      <div
                          class="grid-cell"
                          :class="{ 'na-text': !getMetricValue(item, period, 'prev') }"
                      >
                        <div class="grid-cell-inner">
                          <span>{{ getPeriodLabel(period).prev }}</span>
                          <span class="numcomparePrev">{{ formatMetric(getMetricValue(item, period, 'prev')) }}</span>
                        </div>
                      </div>

                      <div class="grid-cell growth-cell">
                        <template v-if="getGrowthData(item, period)">
                          <img
                              :src="getGrowthData(item, period).diff > 0
                              ? require('@/assets/upward.png')
                              : require('@/assets/downward.png')"
                              alt="growth"
                              class="growth-icon"
                          />
                          {{ getGrowthData(item, period).growth }}%
                        </template>
                        <template v-else>
                          <span class="na-text">NA</span>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="selectedVal==='Date Range'" class="metrics-grid-container">
                  <div class="grid-row1 grid-header">
                    <div class="grid-cell">
                      {{ new Date(dateRange[0]).toLocaleDateString('en-GB') }} to
                      {{ new Date(dateRange[dateRange.length - 1]).toLocaleDateString('en-GB') }}
                    </div>
                  </div>
                  <div class="grid-row1">
                    <div class="grid-cell">
                      <span class="numcompare">{{ formatMetric(item.metric1) }}</span>
                    </div>
                  </div>
                </div>
                <div v-for="(val, i) in level2Data[item.dim2_name]" :key="i">
                  <div class="expand-row" @click="toggleNestedCollapse(item,val)">
                    <div class="product-title-container">
                      <span v-if="nestedCollapseOpen[`${item.dim2_name}_${val.dim3_name}`]" class="dropdown_image_color"
                            style="margin-left: 5px;color: #3164f4;margin-right: 10px">▲</span>
                      <span v-else style="margin-left: 10px;color: #3164f4;margin-right: 5px">▼</span>
                      <span class="prodSku">{{ val.dim3_name }}</span>
                    </div>
                    <!-- Date Range: only metric1, no curr/prev/growth -->
                    <div v-if="isDateRange" class="product-metrics">
                      <div class="metric">
                        <span class="metric-value" :class="{ 'grey-text': !val.metric1 }">
                          {{ formatMetric(val.metric1) }}
                        </span>
                      </div>
                    </div>
                    <div
                        v-else-if="viz1.viz_specific7.sub_parent_headers && viz1.viz_specific7.sub_parent_headers.length"
                        class="product-metrics"
                    >
                      <div
                          class="metric"
                          v-for="(child, index) in viz1.viz_specific7.sub_parent_headers"
                          :key="index"
                      >
                        <!-- Normal metrics -->
                        <template v-if="child.title !== 'percent'">
                          <span v-if="child.title ==='time_period'"
                                class="metric-label">{{ getPeriodLabel(child.period)[child.value] }}</span>
                          <span v-else class="metric-label">{{ child.title }}:</span>
                          <span
                              v-if="child.title==='time_period'"
                              class="metric-value"
                              :class="{ 'grey-text': !val[child.metric] }"
                          >
                        {{ formatMetric(getMetricValue(val, child.period, child.value)) }}
                      </span>
                          <span
                              v-else
                              class="metric-value"
                              :class="{ 'grey-text': !val[child.metric] }"
                          >
                        {{ formatMetric(val[child.metric]) }}
                      </span>
                        </template>

                        <!-- Percent metric special case -->
                        <template v-else>
                          <span class="metric-label"></span>
                          <span
                              class="metric-value"
                              :class="{
                            'green-text': (val[viz1.viz_specific7.sub_parent_headers[0].metric] - val[viz1.viz_specific7.sub_parent_headers[1].metric]) > 0,
                            'red-text': (val[viz1.viz_specific7.sub_parent_headers[0].metric] - val[viz1.viz_specific7.sub_parent_headers[1].metric]) < 0,
                            'grey-text': !(val[viz1.viz_specific7.sub_parent_headers[0].metric] - val[viz1.viz_specific7.sub_parent_headers[1].metric])
                          }"
                          >
                        {{
                              calculateGrowth(
                                  val[viz1.viz_specific7.sub_parent_headers[0].metric],
                                  val[viz1.viz_specific7.sub_parent_headers[1].metric]
                              )
                            }}%
                      </span>
                        </template>
                      </div>
                    </div>
                    <div v-else class="product-metrics">
                      <div class="metric">
                        <div style="display:flex;justify-content:center;gap:10px">
                          <span>{{ getPeriodLabel('YTD').curr }}</span>
                          <span class="numcompare">{{ formatMetric(getMetricValue(val, 'YTD', 'curr')) }}</span>
                        </div>
                      </div>
                      <div class="metric">
                        <div style="display:flex;justify-content:center;gap:10px">
                          <span>{{ getPeriodLabel('YTD').prev }}</span>
                          <span class="numcomparePrev">{{ formatMetric(getMetricValue(val, 'YTD', 'prev')) }}</span>
                        </div>
                      </div>
                      <div class="metric">
                        <span class="metric-label"></span>
                        <span class="metric-value" :class="{
                        'green-text': (val.metric7-val.metric8) > 0,
                        'red-text': (val.metric7-val.metric8) < 0,
                        'grey-text': (val.metric7-val.metric8)
                      }">
                      {{ getGrowthData(val, 'YTD').growth }}%
                    </span>
                      </div>
                    </div>

                    <!--                  <div  class="content-wrapper">-->
                    <!--                    <span class="numcompare">{{val.metric1}}</span>-->
                    <!--                    <span class="numcompare">{{val.metric2}}</span>-->
                    <!--                  </div>-->
                  </div>
                  <b-collapse :visible="nestedCollapseOpen[`${item.dim2_name}_${val.dim3_name}`]"
                              class="nested-collapse">
                    <div class="dropdown-content">
                      <div>
                        <div class="list_item"
                             v-for="(data, j) in subSegregatedData[`${item.dim2_name}_${val.dim3_name}`]" :key="j">
                          <div class="list-item-name">{{ data.dim5_name }}</div>
                          <div class="list-item-metrics">
                            <div
                                v-if="viz1 && viz1.viz_specific7 && viz1.viz_specific7.last_level_metric1"
                                class="list-item-metric"
                            >
                              {{ formatMetric(data[viz1.viz_specific7.last_level_metric1]) }}
                            </div>
                            <div
                                v-if="viz1 && viz1.viz_specific7 && viz1.viz_specific7.last_level_metric"
                                class="list-item-metric"
                            >
                              {{ formatMetric(data[viz1.viz_specific7.last_level_metric]) }}
                            </div>
                            <div v-else class="list-item-metric">{{ formatMetric(data.metric7) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-collapse>
                </div>
              </b-collapse>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import axios from "axios";
import {inrFormatterV2} from "@/common/functions";
import OrderStatusDashboard from "@/components/OrderStatusDashboard.vue";

export default {
  name: "SalesDashboard",
  components: {DatePicker, OrderStatusDashboard},
  props: {
    salesData: [],
    viz1: Object,
    filters: Object
  },
  watch: {
    viz1: {
      immediate: true,
      deep: true,
      handler(newVal) {
        const subSql = newVal?.viz_specific7?.sub_data_sql;
        if (!subSql) return;
        const keys = Object.keys(subSql);
        if (!keys.length) return;
        // Do NOT reset activeKey on every deep viz1 churn (filter apply mutates viz1).
        // Only init or repair if current key is missing.
        if (!this.activeKey || !subSql[this.activeKey]) {
          this.activeKey = keys[0];
          this.selectedDataSql = subSql[keys[0]] || {};
        } else {
          this.selectedDataSql = subSql[this.activeKey] || this.selectedDataSql;
        }
        this.fetchOrderStatusData();
      }
    },
    salesData: {
      immediate: true, // Run immediately on component creation
      deep: true,     // Watch for nested changes if needed
      handler(newVal) {
        // Parent vizlet always fetches default (first tab) datasql.
        // Never overwrite when user is on Product / Customer / Tree & Cat.
        if (newVal && newVal.length && this.isDefaultTab) {
          this.localSalesData = [...newVal];
        }
      }
    },
    dateRange(newRange) {
      if (newRange && newRange.length === 2) {
        this.selectedVal = 'Date Range';
        this.updateDatePickerStyle();
        this.getDateRange();
      } else if (!newRange || !newRange.length) {
        // Cleared via datepicker clear (x) — allow a new range selection
        this.selectedVal = 'MTD';
        this.updateDatePickerStyle();
      }
    },
    filters: {
      immediate: true,
      deep: true,
      handler() {
        this.fetchOrderStatusData();
        // Always reload the currently selected tab with latest filters
        this.refetchActiveTabData();
      }
    }
  },
  data: () => ({
    noteType: 'generic',
    noteDetails: '',
    currentRating: 0,
    activeKey: null,
    activeTabFetchId: 0,
    nestedCollapseOpen: {},
    subSegregatedData: {},
    noteOptions: [
      {value: 'generic', text: 'Generic'},
      {value: 'inventory', text: 'Inventory'},
      {value: 'about', text: 'About'},
      {value: 'store', text: 'Store'}
    ],
    selectedTag: null, // This will hold the selected value
    tagOptions: [
      {text: 'OnTrack', value: 'OnTrack'},
      {text: 'RedFlag', value: 'RedFlag'},
      {text: 'Validate', value: 'Validate'}
    ],
    // salesData: [],
    datepickerStyle: {
      '--dp-background-color': '#E4E5F9',
      '--dp-icon-color': '#083DB1',
    },
    selectedVal: 'MTD',
    dateRange: null,
    today: new Date(),
    year: new Date().getFullYear(),
    preYear: new Date().getFullYear() - 1,
    month: new Date().getMonth() + 1,
    monthName: new Intl.DateTimeFormat('en-US', {month: 'long'}).format(new Date()),
    page1Flag: true,
    code: "",
    isOpen: [],
    localSalesData: [], // Create local copy
    selectedDataSql: {},
    level2Data: {},
    formData: {},
    orderStatusKpiData: [],
    orderStatusRows: []
  }),
  computed: {
    showOrderStatusDashboard() {
      const kpis = this.viz1?.viz_specific7?.kpi;
      return Array.isArray(kpis) && kpis.length > 0;
    },
    orderStatusKpiConfig() {
      return this.viz1?.viz_specific7?.kpi || [];
    },
    orderDashboardTitle() {
      return this.viz1?.viz_specific7?.order_dashboard_title || "";
    },
    salesDashboardTitle() {
      return this.viz1?.viz_specific7?.sales_dashboard_title ||
          this.viz1?.viz_specific7?.metric_name_display ||
          "Sales Dashboard";
    },
    isMobileDevice() {
      return String(this.$store?.state?.deviceType || "").toLowerCase() === "mobile";
    },
    orderStatusHeaderMap() {
      if (this.isMobileDevice) {
        return this.viz1?.viz_specific7?.mobile_table_headers || {};
      }
      return this.viz1?.reportHeaderColumnDp ||
          this.viz1?.report_header_column_dp ||
          this.viz1?.viz_specific7?.report_header_column_dp ||
          {};
    },
    isDefaultTab() {
      const keys = Object.keys(this.viz1?.viz_specific7?.sub_data_sql || {});
      return !this.activeKey || this.activeKey === keys[0];
    },
    segregatedData() {
      console.log(this.aggregateData(this.localSalesData), this.viz1, this.filters);
      return this.aggregateData(this.localSalesData)
    },
    isDateRange() {
      return this.selectedVal === 'Date Range';
    },
    dateRangeMetric1Total() {
      return this.segregatedData.reduce((sum, item) => {
        return sum + (Number(item.metric1) || 0);
      }, 0);
    },
    hasAggregationTotals() {
      if (this.isDateRange) return this.segregatedData.length > 0;
      return (this.viz1?.viz_specific7?.parent_headers || [])
          .some(h => h.aggregation === true);
    },
    aggregationTotals() {
      const headers = this.viz1?.viz_specific7?.parent_headers || [];
      if (!this.hasAggregationTotals) return [];

      const sumMetric = (metric) => {
        if (!metric) return 0;
        return this.segregatedData.reduce((sum, item) => {
          return sum + (Number(item[metric]) || 0);
        }, 0);
      };

      return headers.map((header) => {
        // Keep column slot so aggregates align with parent_headers
        if (!header.aggregation) {
          return { empty: true };
        }

        // percent: growth of first two parent_header metrics, no title
        if (header.title === 'percent') {
          const currTotal = sumMetric(headers[0]?.metric);
          const prevTotal = sumMetric(headers[1]?.metric);
          const growth = this.calculateGrowth(currTotal, prevTotal);
          return {
            empty: false,
            isPercent: true,
            display: `${growth}%`,
            diff: currTotal - prevTotal
          };
        }

        return {
          empty: false,
          isPercent: false,
          title: header.title === 'time_period'
              ? this.getPeriodLabel(header.period)[header.value]
              : header.title,
          metric: header.metric,
          total: sumMetric(header.metric)
        };
      });
    },
    hasValidActions() {
      const actions = this.viz1?.viz_specific7?.actions
      console.log('actions:', actions)
      return Array.isArray(actions) && actions.length > 1
    }
    // firstDimName() {
    //   return this.localSalesData[0] ? this.localSalesData[0].dim1_name : '';
    // }
  },
  methods: {
    async fetchOrderStatusData() {
      if (!this.showOrderStatusDashboard) {
        this.orderStatusKpiData = [];
        this.orderStatusRows = [];
        return;
      }

      const vizSpecific = this.viz1?.viz_specific7 || {};
      const commonSuffix = '/CommonVizClass';
      const requestPayload = this.filters || {};

   
      if (vizSpecific.kpi_datasql_id) {
          axios.post(`/api/fetchDataWithParam/${vizSpecific.kpi_datasql_id}${commonSuffix}`, requestPayload).then(resp => {
            console.log('orderStatusKpiData', resp);
            this.orderStatusKpiData = resp.data;
          });
      }
      
      if (vizSpecific.order_datasql_id) {
         axios.post(`/api/fetchDataWithParam/${vizSpecific.order_datasql_id}${commonSuffix}`, requestPayload).then(resp => {
          console.log('orderStatusRows', resp);
          this.orderStatusRows = resp.data;
         });
      }
    },
    formatMetric(num) {
      if (num === null || num === undefined || num === '' || num === 'NA') return 'NA';
      const menuId = this.$route.params.submenuId;
      return inrFormatterV2(Number(num), menuId);
    },
    initForm() {
      this.formData = {}
      this.viz1.viz_specific7.actions.forEach(a => {
        this.$set(this.formData, a.input_key, null)
      })
    },
    onSubmit() {
      let submenuParam = this.$route.params.submenuId || "RDemo1"
      let allVizData = this.$store.state.fwConfigScreenElement.filter(m => m.menuId === submenuParam)
      let screenName = this.$store.state.menuMasterFlat.filter(m => m.menuId === submenuParam).map(m => m.menuName)[0]
      const payload = this.formData
      const url = "/api/businessActions";
      const recordIdentification = submenuParam +"_" +allVizData[0].vizletTitleDpName
      const data = {
        userId: this.$store.state.userId,
        userName: this.$store.state.userName,
        menuName: screenName,
        screenletName: allVizData[0].screenletTitleDpName,
        vizletName: allVizData[0].vizletTitleDpName,
        recordIdentification: [recordIdentification],
        annotation: '',
        tagStatus: this.selectedTag,
        assignedTo: '',
        bizInputValues: payload
      };
      console.log(payload, url, data);
      axios.post(url, data).then(
          () => {
            console.log('success');
          },
          (e) => {
            console.log("Post annotation", data)
            this.$commonFn.auditLog(screenName, allVizData[0].screenletTitleDpName, "", "POST", e);
            console.error("error in updating annotations", e);
          }
      )
      this.$bvModal.hide('comment-modal');
      this.resetForm();
    },
    resetForm() {
      this.formData ={}
    },
    setRating(rating) {
      this.currentRating = rating;
    },
    commentBox() {
      this.$bvModal.show('comment-modal');
    },
    calAchievedPerc(current, previous) {
      const growth = ((previous - current) / current) * 100;
      return Math.abs(growth).toFixed(1);
    },
    toggleNestedCollapse(item, val) {
      // Create a unique key: dim2_name + dim3_name
      const collapseKey = `${item.dim2_name}_${val.dim3_name}`;

      this.$set(this.nestedCollapseOpen, collapseKey, !this.nestedCollapseOpen[collapseKey]);

      // Fetch and store sub-segregated data
      const dataKey = collapseKey; // Same key can be reused
      const filteredData = this.localSalesData.filter(
          (x) => x.dim2_name === item.dim2_name && x.dim3_name === val.dim3_name
      );
      console.log("filteredData", filteredData);

      // Aggregate data by dim5_name, summing metric7
      const aggregatedData = filteredData.reduce((acc, current) => {
        const key = current.dim5_name;

        if (!acc[key]) {
          // First occurrence - create new object
          acc[key] = {...current};
        } else {
          // Subsequent occurrence - sum metric7 and other metrics if needed
          acc[key].metric7 += current.metric7;
          // Add other metrics if you need to aggregate them too
          // acc[key].metric1 += current.metric1;
          // acc[key].metric2 += current.metric2;
          // acc[key].metric3 += current.metric3;
          // acc[key].metric8 += current.metric8;
          // acc[key].metric15 += current.metric15;
        }

        return acc;
      }, {});

      // Convert the aggregated object back to array
      const uniqueAggregatedData = Object.values(aggregatedData);

      // Dynamic sort by DB config
      this.sortResult(
          uniqueAggregatedData,
          this.viz1.viz_specific7?.level3_sort_by,
          this.viz1.viz_specific7?.level3_sort_order
      );

      this.$set(this.subSegregatedData, dataKey, uniqueAggregatedData);
    },
    handleKeyClick(key) {
      console.log("key", key)
      this.nestedCollapseOpen = {};
      this.isOpen = [];
      this.subSegregatedData = {};
      this.level2Data = {};
      this.localSalesData = [];
      this.selectedDataSql = {};
      this.selectedVal = 'MTD';
      this.dateRange = null;
      this.today = new Date();
      this.year = new Date().getFullYear();
      this.preYear = new Date().getFullYear() - 1;
      this.month = new Date().getMonth() + 1;
      this.monthName = new Intl.DateTimeFormat('en-US', {month: 'long'}).format(new Date());
      this.activeKey = key;
      const selectedItem = this.viz1.viz_specific7.sub_data_sql[key];
      console.log(selectedItem);
      this.selectedDataSql = selectedItem
      this.refetchActiveTabData();
      this.dateRange = ""
      this.selectedVal = ""
      const menuDetails = this.$store.state.menuMasterFlat.filter(m => m.menuId === 'RDemo1')
      console.log("menuDetails", menuDetails);
      this.$commonFn.auditLog(menuDetails[0].syspk, 'RDemo1', menuDetails[0].menuName, menuDetails[0].menuName, "user-clk", "Success", this.$store.state.deviceType)
      // Handle your click logic here
    },
    /** Fetch using active tab's data_sql_id + current filters (not parent default Branch sql). */
    refetchActiveTabData() {
      const tabKey =
          this.activeKey ||
          Object.keys(this.viz1?.viz_specific7?.sub_data_sql || {})[0];
      const tabConfig =
          this.viz1?.viz_specific7?.sub_data_sql?.[tabKey] ||
          this.selectedDataSql;
      if (!tabConfig?.data_sql_id) return;

      // Date Range uses its own sql + dimension from selected tab
      if (this.selectedVal === 'Date Range' && this.dateRange && this.dateRange.length === 2) {
        this.getDateRange();
        return;
      }

      const requestId = ++this.activeTabFetchId;
      const url = '/api/fetchDataWithParam/' + tabConfig.data_sql_id + '/' + 'CommonVizClass';
      axios.post(url, this.filters || {}).then(resp => {
        // Ignore stale responses if user switched tabs / filters again
        if (requestId !== this.activeTabFetchId) return;
        if (this.activeKey && this.activeKey !== tabKey) return;
        this.$set(this, 'localSalesData', resp.data);
        this.nestedCollapseOpen = {};
        this.isOpen = [];
        this.subSegregatedData = {};
        this.level2Data = {};
      }).catch(error => {
        console.error("Active tab fetch error:", error);
      });
    },
    getPeriodLabel(period) {
      // Date Range: labels follow selected range start (metric1=curr, metric2=prev)
      // e.g. 01/07/2026–31/07/2026 → Jul 26 / Jun 26
      // e.g. 01/01/2025–31/12/2025 → Jan 25 / Dec 24
      let now;
      if (
          this.selectedVal === 'Date Range' &&
          this.dateRange &&
          this.dateRange.length >= 2
      ) {
        const d0 = new Date(this.dateRange[0]);
        const d1 = new Date(this.dateRange[this.dateRange.length - 1]);
        now = d0 <= d1 ? d0 : d1;
      } else {
        const dateValue = this.viz1?.viz_specific7?.date_field_value;
        now = dateValue ? new Date(dateValue) : new Date();
      }
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth(); // 0-11
      const prevYear = currentYear - 1;
      const currentYearShort = String(currentYear).slice(-2);
      const prevYearShort = String(prevYear).slice(-2);

      const calendarType = String(
          this.viz1?.viz_specific7?.calender || this.viz1?.viz_specific7?.calendar || ''
      ).toLowerCase();
      const isFiscalCalendar = calendarType === 'fiscal';

      // Fiscal year starts in April (month index 3)
      const fiscalYearStartMonth = 3;

      let labels;

      // Month names
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      let quarterContext;
      if (isFiscalCalendar) {
        const monthsSinceFiscalStart = (currentMonth - fiscalYearStartMonth + 12) % 12;
        const currQuarter = Math.floor(monthsSinceFiscalStart / 3) + 1;
        const currQuarterYear = currentMonth >= fiscalYearStartMonth ? currentYear : currentYear - 1;
        const prevQuarter = currQuarter === 1 ? 4 : currQuarter - 1;
        const prevQuarterYear = currQuarter === 1 ? currQuarterYear - 1 : currQuarterYear;
        quarterContext = {
          currQuarter,
          prevQuarter,
          currQuarterYearShort: String(currQuarterYear).slice(-2),
          prevQuarterYearShort: String(prevQuarterYear).slice(-2),
          prevYearShort: String(currQuarterYear - 1).slice(-2)
        };
      } else {
        const currQuarter = Math.floor(currentMonth / 3) + 1;
        const prevQuarter = currQuarter === 1 ? 4 : currQuarter - 1;
        const prevQuarterYear = currQuarter === 1 ? currentYear - 1 : currentYear;
        quarterContext = {
          currQuarter,
          prevQuarter,
          currQuarterYearShort: currentYearShort,
          prevQuarterYearShort: String(prevQuarterYear).slice(-2),
          prevYearShort
        };
      }

      switch (period) {
        case 'MTD':
          labels = {
            curr: `${monthNames[currentMonth]} ${currentYearShort}`,
            prev: `${monthNames[(currentMonth - 1 + 12) % 12]} ${(currentMonth === 0 ? prevYear : currentYear).toString().slice(-2)}`
          };
          break;
        case 'PMTD':
          labels = {
            curr: `${monthNames[currentMonth]} ${currentYearShort}`,
            prev: `${monthNames[currentMonth]} ${prevYearShort}`
          };
          break;
        case 'QTD':
          labels = {
            curr: `Q${quarterContext.currQuarter} ${quarterContext.currQuarterYearShort}`,
            prev: `Q${quarterContext.prevQuarter} ${quarterContext.prevQuarterYearShort}`
          };
          break;
        case 'PQTD':
          labels = {
            curr: `Q${quarterContext.currQuarter} ${quarterContext.currQuarterYearShort}`,
            prev: `Q${quarterContext.currQuarter} ${quarterContext.prevYearShort}`
          };
          break;
        case 'YTD':
          labels = {
            curr: isFiscalCalendar ? `CFY ${quarterContext.currQuarterYearShort}` : `CCY${quarterContext.currQuarterYearShort}`,
            prev: isFiscalCalendar ? `PFY ${quarterContext.prevYearShort}` : `PCY${quarterContext.prevYearShort}`
          };
          break;
        default:
          return period;
      }
      return labels;
    },
    getMetricValue(item, period, type) {
      // Define the metric mapping
      const metricMap = {
        MTD: {
          curr: 'metric1',  // Current Month
          prev: 'metric2'  // Previous Month
        },
        PMTD: {
          curr: 'metric1',   // Current Month
          prev: 'metric4'   // Same Month Previous Year
        },
        QTD: {
          curr: 'metric13',  // Current Quarter
          prev: 'metric9'   // Previous Quarter
        },
        PQTD: {
          curr: 'metric13',  // Current Quarter
          prev: 'metric14'  // Same Quarter Previous Year
        },
        YTD: {
          curr: 'metric7',    // Current Fiscal Year
          prev: 'metric8'    // Previous Fiscal Year
        }
      };

      // Validate inputs
      if (!metricMap[period] || !metricMap[period][type]) {
        console.error(`Invalid period or type: ${period}, ${type}`);
        return null;
      }

      // Get the metric key
      const metricKey = metricMap[period][type];

      // Return the value or 'NA' if not available
      return item[metricKey] !== undefined && item[metricKey] !== null
          ? item[metricKey]
          : 'NA';
    },
    getGrowthData(item, period) {
      const growthMap = {
        MTD: ['metric1', 'metric2'],  // metric1 = current, metric3 = previous
        PMTD: ['metric1', 'metric4'],
        QTD: ['metric13', 'metric9'],
        PQTD: ['metric13', 'metric14'],
        YTD: ['metric7', 'metric8']
      };

      const [currentKey, previousKey] = growthMap[period];
      const current = item[currentKey];
      const previous = item[previousKey];

      const growth = this.calculateGrowth(current, previous);
      const achieved = this.calAchievedPerc(item.metric15, current);
      return {
        growth: growth,
        diff: current - previous,
        achieved: achieved
      };
    },
    calculateGrowth(current, previous) {
      if (!previous || previous === 0) return 'NA';
      const growth = ((current - previous) / previous) * 100;
      return Math.abs(growth).toFixed(1); // Absolute value for display
    },
    toggleCollapse(index, val) {
      this.$set(this.isOpen, index, !this.isOpen[index]);
      if (!this.isOpen[index]) {
        Object.keys(this.nestedCollapseOpen).forEach(key => {
          if (key.startsWith(`${val.dim2_name}_`)) {
            this.$set(this.nestedCollapseOpen, key, false);
          }
        });
      }
      const data = this.localSalesData.filter(item => item.dim2_name === val.dim2_name)
      console.log(this.level2aggregateData(data))
      this.$set(this.level2Data, val.dim2_name, this.level2aggregateData(data));
    },
    aggregateData(rawData) {
      // Create a map to store aggregated values
      const aggregatedMap = new Map();

      rawData.forEach(item => {
        // Create a unique key for each combination of dimensions
        const key = `${item.dim2_name}`;

        if (!aggregatedMap.has(key)) {
          // Initialize new entry if it doesn't exist
          aggregatedMap.set(key, {
            dim1_id: item.dim1_id,
            dim1_name: item.dim1_name,
            dim2_name: item.dim2_name,
            dim3_name: item.dim3_name,
            dim4_name: item.dim4_name,
            dim5_name: item.dim5_name,
            metric1: 0,
            metric2: 0,
            metric3: 0,
            metric4: 0,
            metric5: 0,
            metric6: 0,
            metric7: 0,
            metric8: 0,
            metric9: 0,
            metric10: 0,
            metric11: 0,
            metric12: 0,
            metric13: 0,
            metric14: 0,
            metric15: 0,
          });
        }

        // Get the existing entry
        const entry = aggregatedMap.get(key);

        // Sum the metrics (convert strings to numbers first)
        entry.metric1 += Number(item.metric1) || 0;
        entry.metric2 += Number(item.metric2) || 0;
        entry.metric3 += Number(item.metric3) || 0;
        entry.metric4 += Number(item.metric4) || 0;
        entry.metric5 += Number(item.metric5) || 0;
        entry.metric6 += Number(item.metric6) || 0;
        entry.metric7 += Number(item.metric7) || 0;
        entry.metric8 += Number(item.metric8) || 0;
        entry.metric9 += Number(item.metric9) || 0;
        entry.metric10 += Number(item.metric10) || 0;
        entry.metric11 += Number(item.metric11) || 0;
        entry.metric12 += Number(item.metric12) || 0;
        entry.metric13 += Number(item.metric13) || 0;
        entry.metric14 += Number(item.metric14) || 0;
        entry.metric15 += Number(item.metric15) || 0;
      });

      // Convert the map values to an array
      const result = Array.from(aggregatedMap.values());

      // Dynamic sort by DB config
      this.sortResult(
          result,
          this.viz1.viz_specific7?.level1_sort_by,
          this.viz1.viz_specific7?.level1_sort_order
      );

      return result;
    },
    level2aggregateData(rawData) {
      // Create a map to store aggregated values
      const aggregatedMap = new Map();

      rawData.forEach(item => {
        // Create a unique key for each combination of dimensions
        const key = `${item.dim2_name}-${item.dim3_name}`;

        if (!aggregatedMap.has(key)) {
          // Initialize new entry if it doesn't exist
          aggregatedMap.set(key, {
            dim1_id: item.dim1_id,
            dim1_name: item.dim1_name,
            dim2_name: item.dim2_name,
            dim3_name: item.dim3_name,
            dim4_name: item.dim4_name,
            dim5_name: item.dim5_name,
            metric1: 0,
            metric2: 0,
            metric3: 0,
            metric4: 0,
            metric5: 0,
            metric6: 0,
            metric7: 0,
            metric8: 0,
            metric9: 0,
            metric10: 0,
            metric11: 0,
            metric12: 0,
            metric13: 0,
            metric14: 0,
            metric15: 0,
          });
        }

        // Get the existing entry
        const entry = aggregatedMap.get(key);

        // Sum the metrics (convert strings to numbers first)
        entry.metric1 += Number(item.metric1) || 0;
        entry.metric2 += Number(item.metric2) || 0;
        entry.metric3 += Number(item.metric3) || 0;
        entry.metric4 += Number(item.metric4) || 0;
        entry.metric5 += Number(item.metric5) || 0;
        entry.metric6 += Number(item.metric6) || 0;
        entry.metric7 += Number(item.metric7) || 0;
        entry.metric8 += Number(item.metric8) || 0;
        entry.metric9 += Number(item.metric9) || 0;
        entry.metric10 += Number(item.metric10) || 0;
        entry.metric11 += Number(item.metric11) || 0;
        entry.metric12 += Number(item.metric12) || 0;
        entry.metric13 += Number(item.metric13) || 0;
        entry.metric14 += Number(item.metric14) || 0;
        entry.metric15 += Number(item.metric15) || 0;
      });

      // Convert the map values to an array
      const result = Array.from(aggregatedMap.values());

      // Prefer tab-level sort_by (e.g. product.sort_by) over global level2_sort_by
      const tabConfig =
          this.viz1?.viz_specific7?.sub_data_sql?.[this.activeKey] ||
          this.selectedDataSql ||
          {};
      const sortBy = tabConfig.sort_by || this.viz1.viz_specific7?.level2_sort_by;
      const sortOrder = tabConfig.sort_order || this.viz1.viz_specific7?.level2_sort_order;

      this.sortResult(result, sortBy, sortOrder);

      return result;
    },
    sortResult(result, sortKey, sortOrder) {
      if (!sortKey) {
        // Default sort by dim2_name alphabetically
        return result.sort((a, b) => a.dim2_name.localeCompare(b.dim2_name));
      }

      // Support multi-key sort: "metric1, metric2" + "desc, asc"
      const keys = String(sortKey).split(',').map(k => k.trim()).filter(Boolean);
      const orders = sortOrder
          ? String(sortOrder).split(',').map(o => o.trim().toLowerCase())
          : [];

      const compareByKey = (a, b, key, order) => {
        const dir = order === 'asc' ? 1 : -1;
        if (key.startsWith('metric')) {
          return ((a[key] ?? 0) - (b[key] ?? 0)) * dir;
        }
        return (a[key] || '').localeCompare(b[key] || '') * dir;
      };

      return result.sort((a, b) => {
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          // Per-key order, else single shared order, else metric→desc / else→asc
          const order = (
              orders[i] ||
              orders[0] ||
              (key.startsWith('metric') ? 'desc' : 'asc')
          ).toLowerCase();
          const cmp = compareByKey(a, b, key, order);
          if (cmp !== 0) return cmp;
        }
        return 0;
      });
    },
    updateDatePickerStyle() {
      if (this.selectedVal === 'Date Range') {
        this.datepickerStyle['--dp-background-color'] = '#083DB1';
        this.datepickerStyle['--dp-icon-color'] = 'white';
      } else {
        this.datepickerStyle['--dp-background-color'] = '#E4E5F9';
        this.datepickerStyle['--dp-icon-color'] = '#083DB1';
      }
    },
    disableFutureDates(date) {
      const today = new Date();
      return date > today;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    getDateRange() {
      const [fromDate, toDate] = this.dateRange;

      // Safely get the first dim2_name from sub_data_sql
      const firstDimName = this.viz1.viz_specific7.sub_data_sql
          ? Object.values(this.viz1.viz_specific7.sub_data_sql)[0]?.dim2_name
          : 'default_dim_name'; // Provide a fallback

      const payload = {
        DateConfig: {
          dateField: [this.viz1.viz_specific7?.dateField],
          dynamicField: ['&dynamic_col1'],
          dimensionField: [this.selectedDataSql?.dim2_name || firstDimName],
          dateRange: {
            fromDate: this.formatDate(fromDate),
            toDate: this.formatDate(toDate)
          }
        }
      };

      let passedParam = {...this.filters, ...payload};

      // Safely get the date_range_sql_id
      const datasql = this.viz1.viz_specific7?.date_range_sql_id || 'default_sql_id';
      const url = '/api/fetchDataWithParam/' + datasql + '/' + 'CommonVizClass';

      axios.post(url, passedParam).then(resp => {
        this.$set(this, 'localSalesData', resp.data);
      }).catch(error => {
        console.error("API Error:", error);
      });
    }
  }
}
</script>
<style scoped>
.star-rating {
  display: inline-flex;
  gap: 4px;
}

.star {
  font-size: 24px;
  color: #ccc;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
}

.star.selected {
  color: gold;
  background: rgba(255, 215, 0, 0); /* Light gold background */
}

.star:hover {
  transform: scale(1.2);
}

/* Base Styles - Mobile First Approach */
.sales-dashboard-container {
  font-family: Arial, sans-serif;
  width: 100%;
  max-width: 1200px;
  background-color: aliceblue;
  border-radius: 30px;
  margin: 0 auto;
  padding: 15px;
  box-sizing: border-box;
}


.sales-title-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  margin-bottom: 6px;
}

.sales-left-title {
  color: #083DB1;
  font-size: 18px;
  font-weight: 700;
  text-align: left;
  padding-left: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.key-row {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  justify-content: center;
  align-items: center;
}

.key-item {
  padding: 4px 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 16px;
  text-align: center;
  font-weight: 600;
}

.key-item:hover {
  color: rgb(0 105 217);
}

.sales-dashboard-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 5px;
  width: 100%;
}

.comment-button {
  background: none;
  border: none;
  cursor: pointer;
  color: cornflowerblue; /* Adjust color to match your design */
  font-size: 1.2rem; /* Adjust size as needed */
  padding: 5px;
}

.dashboard-title {
  font-size: clamp(1rem, 4vw, 1rem);
  font-weight: bold;
  margin-bottom: 15px;
  text-align: left;
  color: #083DB1;
  width: 100%;
  padding-left: 8px;
}

.time-period-selector {
  width: 100%;
  max-width: 200px;
  display: flex;
  justify-content: center;
}

.responsive-datepicker {
  width: 100% !important;
}

.dashboard-content {
  width: 100%;
}

.schema-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
}

.schema-label {
  font-weight: bold;
  margin-bottom: 8px;
  font-size: clamp(1rem, 3vw, 1.2rem);
}

.date-range-label {
  color: #666;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
}

.sales-data-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.aggregation-summary {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 3px 8px; /* match .product-header */
  margin-bottom: 6px;
  gap: 8px;
}

.aggregation-spacer {
  visibility: hidden;
  pointer-events: none;
}

.aggregation-item {
  font-size: clamp(1rem, 3.5vw, 1rem);
  font-weight: 600;
  color: #083DB1;
  white-space: nowrap;
}

.aggregation-item .metric-label,
.aggregation-item .metric-value {
  color: inherit;
  font-weight: inherit;
}

.aggregation-empty {
  visibility: hidden;
}

.product-list {
  width: 100%;
}

.product-item {
  margin-bottom: 5px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.product-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.product-header {
  display: flex;
  align-items: center;
  padding: 3px 8px;
  background-color: #E4E5F9;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.product-header:hover {
  background-color: #D6D8F0;
}

.product-metrics {
  display: flex;
  gap: 24px;
  flex-wrap: nowrap;
  margin-left: auto;
}

.metric {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: clamp(12px, 2.5vw, 14px);
  min-width: 120px;
}

.metric-label {
  font-weight: 500;
  color: #555;
  white-space: nowrap;
}

.metric-value {
  font-weight: 600;
  white-space: nowrap;
}

.toggle-icon {
  width: 18px;
  height: 18px;
  margin-right: 12px;
  flex-shrink: 0;
}

.product-name {
  font-weight: 600;
  font-size: clamp(1rem, 3vw, 1.1rem);
  color: #083DB1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-details {
  padding: 5px;
  background-color: #fff;
}

.metrics-grid-container {
  width: 100%;
}

.grid-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3px 8px; /* match .expand-row */
  border-radius: 8px;
  background: #f9f9f9;
  margin-bottom: 3px;
  gap: 8px;
}

.grid-row-spacer {
  flex: 1;
  min-width: 0;
}

.grid-row-metrics {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px; /* match .product-metrics */
  flex-shrink: 0;
  margin-left: auto;
}

.grid-row1 {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  align-items: center;
  border-radius: 8px;
  background: #f9f9f9;
  margin-bottom: 3px;
}

.grid-header {
  font-weight: 600;
  word-wrap: break-word;
  color: white;
  position: sticky;
  left: 0;
}

.grid-cell {
  font-size: 0.9rem;
  color: #333;
  padding: 4px 6px;
  text-align: center;
  min-width: 120px; /* match .metric */
}

.grid-cell-inner {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.numcompare {
  color: #083DB1;
}

.numcomparePrev {
  color: #26dff5
}

.growth-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.na-text {
  color: #999;
  font-style: italic;
}

.growth-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/*.date-range-view {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 15px;
  width: 100%;
}

.product-summary {
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  background-color: #fff;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}

.product-summary:hover {
  background-color: #f5f5f5;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}*/

.product-summary .product-name {
  font-weight: 600;
  margin-bottom: 8px;
  color: #083DB1;
}

.product-summary .product-metric {
  font-size: 1.1rem;
  font-weight: 500;
}

/* Color Classes */
.grey-text {
  color: #999;
}

.green-text {
  color: #28a745;
}

.red-text {
  color: #dc3545;
}


/* Expand row styles */
.dropdown {
  position: absolute;
  color: #3164f4;
  font-size: clamp(14px, 3vw, 18px);
  margin-top: -15px;
  display: flex;
  left: 5px;
  align-items: center;
}

.expand-row {
  display: flex;
  align-items: center;
  padding: 3px 8px;
  background-color: rgb(200 237 253);
  border-radius: 8px;
  margin-bottom: 1px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.content-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 10px; /* Adds space between the text elements */
}

.prodSku {
  margin: 0 auto; /* center horizontally in flex */
  font-size: 16px;
  font-weight: 600;
}

/* Nested collapse styles */
.nested-collapse {
  margin-top: 1px;
}

.dropdown-content {
  background: white;
  border-radius: 4px;
  padding: 8px;
}

.list_item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  gap: 12px;
}

.list-item-name {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.list-item-metrics {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
  margin-left: auto;
}

.list-item-metric {
  min-width: 90px;
  font-weight: 600;
  white-space: nowrap;
  text-align: right;
}

@media (max-width: 360px) {
  .grid-cell {
    font-size: 0.65rem;
  }
}

@media (max-width: 480px) {
  .sales-title-row {
    grid-template-columns: 1fr;
    row-gap: 4px;
    margin-bottom: 4px;
  }

  .sales-left-title {
    font-size: 15px;
    padding-left: 2px;
  }

  .expand-row {
    padding: 4px 6px;
    flex-direction: column;
    align-items: flex-start;
  }

  .product-header {
    padding: 4px 6px;
    flex-direction: column;
    align-items: flex-start;
  }

  .product-title-container {
    display: flex;
    align-items: center;
    margin-bottom: 2px; /* Add some space between title and metrics */
  }

  .product-metrics {
    width: 100%;
  }

  .metric {
    flex-basis: 100%;
    min-width: 20px;
    white-space: nowrap;
  }

  .toggle-icon {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }

  .product-name {
    font-size: 0.95rem;
  }

  .date-range-view {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .product-summary {
    padding: 12px;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    min-height: auto;
  }
}

@media (max-width: 576px) and (min-width: 361px) {

  .product-item {
    margin-bottom: 5px;
  }

  .grid-row {
    padding: 2px 4px;
    grid-template-columns: repeat(3, minmax(80px, 1fr));
  }

  .grid-cell {
    font-size: 0.75rem;
    padding: 2px 4px;
  }

  .date-range-view {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }

  .product-header {
    padding: 4px 6px;
    flex-direction: column;
    align-items: flex-start;
  }

  .prodSku {
    font-size: 12px;
  }

  .expand-row {
    padding: 4px 6px;
    flex-direction: column;
    align-items: flex-start;
  }

  .product-title-container {
    display: flex;
    align-items: center;
    margin-bottom: 2px; /* Add some space between title and metrics */
  }

}

@media (max-width: 768px) {
  /* Mobile: keep original 3-column Current/Previous/Growth grid */
  .grid-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 2px 4px;
  }

  .grid-row-spacer {
    display: none;
  }

  .grid-row-metrics {
    display: contents;
  }

  .grid-cell {
    min-width: 0;
  }

  .expand-row {
    padding: 4px 6px;
    flex-direction: column;
    align-items: flex-start;
  }

  .product-title-container {
    display: flex;
    align-items: center;
    margin-bottom: 2px; /* Add some space between title and metrics */
  }

  .product-header {
    padding: 4px 6px;
    flex-direction: column;
    align-items: flex-start;
  }

  .prodSku {
    font-size: 14px;
  }

  .expand-row {
    padding: 4px 6px;
    font-size: 14px;
    flex-direction: column;
    align-items: flex-start;
  }

  .dropdown-content {
    padding: 2px;
  }

  .list_item {
    padding: 2px 4px;
    font-size: 13px;
  }

  .schema-display {
    align-items: center;
    text-align: center;
  }

  .metric {
    flex-basis: calc(50% - 4px);
  }

  .product-header {
    padding: 4px 6px;
  }

  .date-range-view {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
  }
}

/* Responsive Breakpoints */
@media (max-width: 992px) {
  .date-range-view {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
  }
}
</style>